import React, {
  useContext, useEffect, useCallback, useRef, useState,
} from 'react';
import { useQuery, gql, useMutation } from '@apollo/client';
import {
  Button, Dialog, InputField, LegalText, ErrorMessage, Icon,
} from 'anf-core-react';
import TmntHtml from '../../../tools/TmntHtml';
import useUserState from '../../../tools/UserState/useUserState';
import ProductPageContext from '../../../contexts/ProductPageContext';
import ProductNotificationsLegalLinks from '../ProductNotificationsLegalLinks/ProductNotificationsLegalLinks';
import useEmailValidation from '../ProductNotificationHooks/useEmailValidation';
import useDialogKeyboard from '../ProductNotificationHooks/useDialogKeyboard';
import GET_USER_INFO from '../Gql/userQueries';
import useEmailHash from '../../../tools/useEmailHash';
import getSelectedProductSizeInfo from '../ProductNotificationHooks/getSelectedProductSizeInfo';
import AnalyticsTriggers from '../../../analytics/analyticsTriggers';

const PRODUCT_NOTIFICATION_MUTATION = gql`
  mutation ProductNotificationSignup($email: String!) {
    productNotificationSignup(email: $email) {
      success
      errors {
        message {
          key
          value
        }
        status
      }
    }
  }
`;

const ProductNotificationsDialog = ({
  isOpen,
  onClose,
  brand,
}) => {
  const dialogRef = useRef(null);
  const productPageState = useContext(ProductPageContext);
  const { storeId, tmntData, config } = productPageState;
  const [apiErrorMsg, setApiErrorMsg] = useState('');
  const [dialogStep, setDialogStep] = useState('main');
  const { isLoggedIn } = useUserState(storeId);
  const userIsLoggedIn = isLoggedIn();
  const [productNotificationSignup] = useMutation(PRODUCT_NOTIFICATION_MUTATION);
  const hashEmail = useEmailHash();

  const { data: userInfoData } = useQuery(GET_USER_INFO, {
    fetchPolicy: 'network-only',
    skip: !userIsLoggedIn,
  });

  const {
    emailValue,
    emailInvalidErrMsg,
    validateEmail,
    handleEmailChange,
    handleEmailBlur,
    resetEmail,
    setEmail,
  } = useEmailValidation(tmntData?.productNotificationEmailError?.value);

  const { handleKeyDown } = useDialogKeyboard(dialogRef, onClose);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const container = dialogRef.current.querySelector('.dialog-container');
      if (container) {
        container.focus();
      }
      dialogRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && userIsLoggedIn && userInfoData?.userInfo?.email) {
      setEmail(userInfoData.userInfo.email);
    } else if (!isOpen) {
      resetEmail();
    }
  }, [isOpen, userIsLoggedIn, userInfoData?.userInfo?.email, setEmail, resetEmail]);

  const handleOverlayClick = useCallback((event) => {
    const targetClass = event.target?.classList?.value || '';
    if (targetClass.includes('dialog-wrapper')) {
      onClose();
    }
  }, [onClose]);

  const handleSubmit = useCallback(() => {
    setApiErrorMsg('');
    const isSubmissionValid = validateEmail();
    if (!isSubmissionValid) return;

    productNotificationSignup({
      variables: {
        email: emailValue,
      },
      onCompleted: async (data) => {
        if (data?.productNotificationSignup?.success) {
          setDialogStep('success');

          const emailHash = await hashEmail(emailValue);
          const primarySizeValue = productPageState?.collectionData?.activeProduct?.tileOptions?.primarySize;
          const secondarySizeValue = productPageState?.collectionData?.activeProduct?.tileOptions?.secondarySize;
          const productSizeInfo = getSelectedProductSizeInfo(productPageState);
          AnalyticsTriggers.productNotificationSuccess(
            productPageState,
            {
              email: emailHash,
              primarySizeValue,
              secondarySizeValue,
              productSizeInfo,
            },
          );
        } else {
          setApiErrorMsg(tmntData?.productNotificationError?.value);
        }
      },
    });
  }, [productNotificationSignup, validateEmail, emailValue, tmntData, productPageState, hashEmail]);

  const renderDialogMainContent = useCallback(() => (
    <>
      <h1 id="product-notifications-heading" className="h1 product-notifications-heading">
        {tmntData?.productNotificationTitle?.value}
      </h1>
      <p className="product-notifications-description">
        {tmntData?.productNotificationDescription?.value}
      </p>

      <div className="product-notifications-subscribe-text">
        <InputField
          autoComplete="email"
          id="recognizeCustomerForm_email"
          isInvalid={!!emailInvalidErrMsg}
          isRequired
          label={tmntData?.placeholderEmailText?.value}
          name="email"
          type="email"
          value={emailValue}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
        >
          {emailInvalidErrMsg && (
          <ErrorMessage id="email-invalid-error" variant="error">
            <span>{emailInvalidErrMsg}</span>
          </ErrorMessage>
          )}
        </InputField>
      </div>

      {apiErrorMsg && (
      <ErrorMessage id="api-error-message" variant="error">
        <span>{apiErrorMsg}</span>
      </ErrorMessage>
      )}

      <Button
        classList="product-get-notifications-button"
        variant="secondary"
        brand={brand}
        isFullWidth
        onClick={handleSubmit}
      >
        {tmntData?.getNotificationsButton?.value}
      </Button>

      <div className="product-notifications-legal-text">
        <LegalText>
          <TmntHtml value={tmntData?.productNotificationFooter?.value} />
          <ProductNotificationsLegalLinks config={config} tmntData={tmntData} />
        </LegalText>
      </div>
    </>
  ), [apiErrorMsg, brand, config, emailInvalidErrMsg, emailValue, handleEmailBlur, handleEmailChange, handleSubmit, tmntData]);

  const renderDialogSuccessContent = useCallback(() => (
    <>
      <div className="product-notifications-success-content">
        <h1 className="product-notifications-success-title">{tmntData?.productNotificationSuccess?.value}</h1>
        <Icon icon="checkmark-circle" size="l" />
      </div>
      <Button
        classList="product-get-notifications-button"
        variant="secondary"
        brand={brand}
        isFullWidth
        onClick={onClose}
      >
        {tmntData?.continueButton?.value}
      </Button>
    </>
  ), [brand, onClose, tmntData?.continueButton?.value, tmntData?.productNotificationSuccess?.value]);

  return (
    <Dialog
      ref={dialogRef}
      ariaLabelledBy="product-notifications-heading"
      closeButtonLabel="Close Dialog"
      id="product-notifications-dialog"
      isOpen={isOpen}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      onOverlayClick={handleOverlayClick}
      classList="scope-1892 product-notifications-dialog"
    >
      <div className="product-notifications-dialog-content">
        {dialogStep === 'success' ? renderDialogSuccessContent() : renderDialogMainContent()}
      </div>
    </Dialog>
  );
};

export default ProductNotificationsDialog;
